import { FloatingLabel, InputGroup } from 'react-bootstrap';
import PropTypes from 'prop-types';
import React from 'react';

import './InputLogoField.css';

/**
 * A form field styled as a rounded pill with a leading logo icon and a floating
 * label, shared by the auth and profile forms (log in, sign up, forgot
 * password, create profile, set up username, edit profile). Those pages all
 * rendered this same InputGroup + icon + FloatingLabel skeleton by hand, and
 * each carried its own copy of the styling, which leaked app-wide. This puts the
 * skeleton and its styling in one place.
 *
 * The actual control is passed as children so each page keeps its own
 * `Form.Control` or `Form.Select` with its own value, handlers, and validation
 * props. `append` renders after the control inside the group (used for the
 * show/hide password button). `className` adds classes to the group wrapper.
 */
export default function InputLogoField({
    icon,
    controlId,
    label,
    hasValidation,
    className,
    append,
    children,
}) {
    return (
        <InputGroup
            hasValidation={hasValidation}
            className={`input-logo-label${className ? ` ${className}` : ''}`}
        >
            <img src={icon} alt="" />
            <FloatingLabel controlId={controlId} label={label}>
                {children}
            </FloatingLabel>
            {append}
        </InputGroup>
    );
}

InputLogoField.propTypes = {
    // Leading logo icon source (decorative).
    icon: PropTypes.string.isRequired,
    // Ties the floating label to the control for accessibility.
    controlId: PropTypes.string.isRequired,
    label: PropTypes.node.isRequired,
    // Passed to InputGroup so validation feedback may render as its last child.
    hasValidation: PropTypes.bool,
    // Extra classes for the group wrapper (e.g. 'position-relative', 'w-100').
    className: PropTypes.string,
    // Optional element rendered after the control (e.g. a password toggle).
    append: PropTypes.node,
    // The control itself: a Form.Control or Form.Select.
    children: PropTypes.node.isRequired,
};

InputLogoField.defaultProps = {
    hasValidation: false,
    className: '',
    append: null,
};
